// Shared maintainer/admin endpoint contracts.
import { isOptionalTimeZone, readCommunityTimeZoneRequest } from '../date-time-metadata.js';

export const MAINTAINER_PATHS = {
  me: '/api/maintainer/me',
  overviewStatistics: '/api/maintainer/overview-statistics',
  search: '/api/maintainer/search',
  communities: '/api/maintainer/communities',
  countries: '/api/maintainer/countries',
  states: '/api/maintainer/states',
  regions: '/api/maintainer/regions',
  clusters: '/api/maintainer/clusters',
  bodies: '/api/maintainer/bodies',
  roles: '/api/maintainer/roles',
  rumMetrics: '/api/maintainer/metrics/rum',
  summaryMetrics: '/api/maintainer/metrics/summary',
};

export class MaintainerResponseContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid maintainer ${operation} response: ${detail}`);
    this.name = 'MaintainerResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);

export function readMaintainerCountriesResponse(payload) {
  const validCountry = country => isRecord(country) && typeof country._id === 'string'
    && typeof country.name === 'string'
    && (country.iso2 === undefined || typeof country.iso2 === 'string')
    && (country.iso3 === undefined || typeof country.iso3 === 'string');
  if (!isRecord(payload) || payload.success !== true || !Array.isArray(payload.data)
    || payload.data.some(country => !validCountry(country))) {
    throw new MaintainerResponseContractError('countries', 'expected a success envelope containing identified countries.');
  }
  return payload;
}
const readGeographyRecord = (payload, operation, isValid) => {
  if (!isRecord(payload) || payload.success !== true || !isValid(payload.data)) {
    throw new MaintainerResponseContractError(operation, 'expected a success envelope containing the saved geography record.');
  }
  return payload;
};
export const readMaintainerCountryResponse = payload => readGeographyRecord(payload, 'country', country =>
  isRecord(country) && typeof country._id === 'string' && typeof country.name === 'string'
  && (country.iso2 === undefined || typeof country.iso2 === 'string')
  && (country.iso3 === undefined || typeof country.iso3 === 'string'));

const hasNamedReference = value => isRecord(value) && typeof value._id === 'string' && typeof value.name === 'string';
const readGeographyList = (payload, operation, isValidRow) => {
  if (!isRecord(payload) || payload.success !== true || !Array.isArray(payload.data)
    || payload.data.some(row => !isValidRow(row))) {
    throw new MaintainerResponseContractError(operation, 'expected a success envelope containing identified geography records.');
  }
  return payload;
};

export const readMaintainerStatesResponse = payload => readGeographyList(payload, 'states', row =>
  isRecord(row) && typeof row._id === 'string' && typeof row.name === 'string'
  && (row.code === undefined || typeof row.code === 'string') && hasNamedReference(row.country));
export const readMaintainerStateResponse = payload => readGeographyRecord(payload, 'state', row =>
  isRecord(row) && typeof row._id === 'string' && typeof row.name === 'string'
  && (row.code === undefined || typeof row.code === 'string') && hasNamedReference(row.country));

export const readMaintainerRegionsResponse = payload => readGeographyList(payload, 'regions', row =>
  isRecord(row) && typeof row._id === 'string' && typeof row.name === 'string'
  && typeof row.code === 'string' && hasNamedReference(row.country));
export const readMaintainerRegionResponse = payload => readGeographyRecord(payload, 'region', row =>
  isRecord(row) && typeof row._id === 'string' && typeof row.name === 'string'
  && typeof row.code === 'string' && hasNamedReference(row.country));

export const readMaintainerClustersResponse = payload => readGeographyList(payload, 'clusters', row =>
  isRecord(row) && typeof row._id === 'string' && typeof row.name === 'string'
  && hasNamedReference(row.country)
  && (row.state === null || hasNamedReference(row.state) && typeof row.state.code === 'string')
  && (row.region === null || hasNamedReference(row.region) && typeof row.region.code === 'string'));
export const readMaintainerClusterResponse = payload => readGeographyRecord(payload, 'cluster', row =>
  isRecord(row) && typeof row._id === 'string' && typeof row.name === 'string'
  && hasNamedReference(row.country)
  && (row.state === null || hasNamedReference(row.state) && typeof row.state.code === 'string')
  && (row.region === null || hasNamedReference(row.region) && typeof row.region.code === 'string'));

const isRole = role => isRecord(role) && typeof role._id === 'string' && role._id.length > 0
  && typeof role.name === 'string' && role.name.length > 0
  && ['community', 'global'].includes(role.scope)
  && Array.isArray(role.permissions) && role.permissions.every(permission => typeof permission === 'string');

export const readMaintainerRolesResponse = payload => {
  if (!isRecord(payload) || payload.success !== true || !Array.isArray(payload.data) || !payload.data.every(isRole)) {
    throw new MaintainerResponseContractError('roles', 'expected a success envelope containing identified roles with scope and permissions.');
  }
  return payload;
};

export const readMaintainerRoleResponse = payload => {
  if (!isRecord(payload) || payload.success !== true || !isRole(payload.data)) {
    throw new MaintainerResponseContractError('role', 'expected a success envelope containing an identified role with scope and permissions.');
  }
  return payload;
};

export const readMaintainerRoleUsersResponse = payload => {
  if (!isRecord(payload) || payload.success !== true || !isRole(payload.data?.role)
    || !Array.isArray(payload.data.users) || !payload.data.users.every(user => isRecord(user)
      && typeof user._id === 'string' && user._id.length > 0)) {
    throw new MaintainerResponseContractError('role users', 'expected a success envelope containing the role and identified users.');
  }
  return payload;
};

const isCount = value => Number.isInteger(value) && value >= 0;
export const readMaintainerMeResponse = payload => {
  const data = payload?.data;
  const access = data?.access;
  const validAccess = access === null || access === undefined || isRecord(access)
    && access.mode === 'allowlist' && typeof access.bahaiId === 'string';
  if (!isRecord(payload) || payload.success !== true || !isRecord(data)
    || typeof data.userId !== 'string' || !data.userId.trim() || typeof data.email !== 'string' || !data.email.trim()
    || (data.bahaiId !== undefined && data.bahaiId !== null && typeof data.bahaiId !== 'string')
    || !validAccess) {
    throw new MaintainerResponseContractError('current user', 'expected an identified maintainer and optional allowlist access.');
  }
  return payload;
};

export const readMaintainerOverviewStatisticsResponse = payload => {
  const data = payload?.data;
  const totals = data?.totals;
  const communities = data?.communities;
  const members = data?.members;
  const bodies = data?.bodies;
  const geography = data?.geography;
  const geographyRows = rows => Array.isArray(rows) && rows.every(row => isRecord(row)
    && typeof row.id === 'string' && typeof row.name === 'string'
    && isCount(row.communityCount) && isCount(row.memberCount));
  const validGeography = isRecord(geography)
    && ['countries', 'states', 'regions', 'clusters'].every(key => geographyRows(geography[key]))
    && isRecord(geography.unassigned)
    && ['country', 'state', 'region', 'cluster'].every(key => isRecord(geography.unassigned[key])
      && isCount(geography.unassigned[key].communityCount) && isCount(geography.unassigned[key].memberCount));
  if (!isRecord(payload) || payload.success !== true || !isRecord(data)
    || typeof data.generatedAt !== 'string' || !Number.isFinite(Date.parse(data.generatedAt))
    || !isRecord(totals) || !['communities', 'members', 'bodies', 'countriesRepresented', 'statesRepresented', 'regionsRepresented', 'clustersRepresented'].every(key => isCount(totals[key]))
    || !isRecord(communities) || !isRecord(communities.byVisibility)
    || !['active', 'hidden', 'archived'].every(key => isCount(communities.byVisibility[key]))
    || !Number.isFinite(communities.averageMembers) || !isCount(communities.fullyMapped)
    || !Number.isFinite(communities.geographyCoveragePercentage) || !isCount(communities.missingLocalSpiritualAssembly)
    || !Array.isArray(communities.largest) || !communities.largest.every(row => isRecord(row)
      && typeof row.id === 'string' && typeof row.name === 'string' && isCount(row.memberCount)
      && isRecord(row.location) && ['country', 'state', 'region', 'cluster'].every(key => row.location[key] === null || typeof row.location[key] === 'string'))
    || !isRecord(members) || !Array.isArray(members.byStatus)
    || !members.byStatus.every(row => isRecord(row) && typeof row.status === 'string' && isCount(row.count))
    || !isCount(members.withoutKnownCommunity) || !validGeography
    || !isRecord(bodies) || !isCount(bodies.total) || !isCount(bodies.communitiesMissingLocalSpiritualAssembly)
    || !Array.isArray(bodies.byType) || !bodies.byType.every(row => isRecord(row) && typeof row.type === 'string' && isCount(row.count))) {
    throw new MaintainerResponseContractError('overview statistics', 'expected timestamped totals, visibility, member, geography and body statistics.');
  }
  return payload;
};

const MAINTAINER_SEARCH_CATEGORIES = ['communities', 'states', 'countries', 'members', 'userBodies', 'roles'];
export const readMaintainerSearchResponse = payload => {
  const data = payload?.data;
  const categories = data?.categories;
  const validResult = (category, row) => {
    if (!isRecord(row) || typeof row.id !== 'string' || !row.id || typeof row.label !== 'string'
      || typeof row.description !== 'string' || typeof row.category !== 'string') return false;
    const types = {communities: 'community', states: 'state', countries: 'country', members: 'member', userBodies: 'userBody', roles: 'role'};
    if (row.type !== types[category]) return false;
    return category !== 'roles' || Array.isArray(row.permissions) && row.permissions.every(permission => typeof permission === 'string')
      && Number.isInteger(row.assignedCount) && row.assignedCount >= 0 && Array.isArray(row.assignedUsers);
  };
  if (!isRecord(payload) || payload.success !== true || !isRecord(data) || typeof data.query !== 'string'
    || !isRecord(categories) || !MAINTAINER_SEARCH_CATEGORIES.every(category => Array.isArray(categories[category])
      && categories[category].every(row => validResult(category, row)))
    || !Array.isArray(data.results)
    || JSON.stringify(data.results) !== JSON.stringify(MAINTAINER_SEARCH_CATEGORIES.flatMap(category => categories[category]))) {
    throw new MaintainerResponseContractError('search', 'expected query, typed category lists and their ordered flattened result list.');
  }
  return payload;
};

const isBody = body => isRecord(body) && typeof body._id === 'string' && body._id.length > 0
  && typeof body.name === 'string' && typeof body.type === 'string'
  && ['community', 'national', 'regional'].includes(body.scope);
export const readMaintainerBodiesResponse = payload => {
  if (!isRecord(payload) || payload.success !== true || !Array.isArray(payload.data)
    || !payload.data.every(body => isBody(body) && isCount(body.membershipCount))) {
    throw new MaintainerResponseContractError('bodies', 'expected body records with membership counts.');
  }
  return payload;
};
export const readMaintainerBodyResponse = payload => {
  if (!isRecord(payload) || payload.success !== true || !isBody(payload.data)) {
    throw new MaintainerResponseContractError('body', 'expected a saved body record.');
  }
  return payload;
};
export const readMaintainerBodyDetailResponse = payload => {
  const data = payload?.data;
  const validMembership = membership => isRecord(membership) && typeof membership._id === 'string'
    && typeof membership.role === 'string' && typeof membership.isAdmin === 'boolean'
    && (typeof membership.userId === 'string' || isRecord(membership.userId)
      && typeof membership.userId._id === 'string');
  if (!isRecord(payload) || payload.success !== true || !isRecord(data) || !isBody(data.body)
    || !Array.isArray(data.memberships) || !data.memberships.every(validMembership)) {
    throw new MaintainerResponseContractError('body detail', 'expected a body and identified memberships.');
  }
  return payload;
};

export const readMaintainerRumIngestResponse = payload => {
  const data = payload?.data;
  if (!isRecord(payload) || payload.success !== true || !isRecord(data)
    || !isCount(data.acceptedCount) || !isCount(data.duplicateCount) || !isCount(data.rejectedCount)
    || typeof data.receivedAt !== 'string' || !Number.isFinite(Date.parse(data.receivedAt))) {
    throw new MaintainerResponseContractError('RUM ingest', 'expected non-negative accepted, duplicate, and rejected counts with a receive timestamp.');
  }
  return payload;
};

export const readMaintainerMetricsSummaryResponse = payload => {
  const data = payload?.data;
  const pipeline = data?.pipeline;
  const coverage = data?.applicationCoverage;
  if (!isRecord(payload) || payload.success !== true || !isRecord(data) || data.source !== 'remote'
    || typeof data.generatedAt !== 'string' || !Number.isFinite(Date.parse(data.generatedAt))
    || !isCount(data.sampleCount) || !(data.lastSampleAt === null || typeof data.lastSampleAt === 'string' && Number.isFinite(Date.parse(data.lastSampleAt)))
    || !Array.isArray(data.scorecard) || !isRecord(data.platformMetrics) || !Array.isArray(data.applications)
    || !isRecord(coverage) || !isCount(coverage.expectedCount) || !isCount(coverage.reportingCount)
    || !isCount(coverage.staleCount) || !isCount(coverage.missingCount) || !Array.isArray(data.alerts)
    || !isRecord(pipeline) || typeof pipeline.rumEndpoint !== 'string' || typeof pipeline.summaryEndpoint !== 'string'
    || !(pipeline.lastFlushAt === null || typeof pipeline.lastFlushAt === 'string' && Number.isFinite(Date.parse(pipeline.lastFlushAt)))
    || !['active', 'idle'].includes(pipeline.lastFlushStatus) || !isCount(pipeline.pendingCount)
    || typeof pipeline.speedInsightsEnabled !== 'boolean' || typeof pipeline.gtmEnabled !== 'boolean'
    || !Number.isFinite(pipeline.lookbackDays) || !Array.isArray(data.recentSamples)) {
    throw new MaintainerResponseContractError('metrics summary', 'expected a remote summary snapshot with pipeline state, coverage, and sample collections.');
  }
  return payload;
};

export const readMaintainerMemberResponse = payload => {
  const member = payload?.data;
  if (!isRecord(payload) || payload.success !== true || !isRecord(member)
    || typeof member._id !== 'string' || typeof member.bahaiId !== 'string'
    || typeof member.firstName !== 'string' || typeof member.lastName !== 'string'
    || typeof member.region !== 'string' || typeof member.state !== 'string'
    || typeof member.country !== 'string' || typeof member.status !== 'string') {
    throw new MaintainerResponseContractError('member', 'expected the updated member identity and directory fields.');
  }
  return payload;
};

export const readMaintainerRoleAssignmentResponse = payload => {
  const user = payload?.data?.user;
  if (!isRecord(payload) || payload.success !== true || !isRecord(user)
    || typeof user.id !== 'string' || !user.id || !Array.isArray(user.roles)
    || !user.roles.every(role => typeof role === 'string') || !Array.isArray(user.roleAssignments)
    || !user.roleAssignments.every(assignment => isRecord(assignment)
      && ['global', 'community'].includes(assignment.scopeType)
      && (typeof assignment.role === 'string' || isRecord(assignment.role)
        && typeof assignment.role.name === 'string'))) {
    throw new MaintainerResponseContractError('role assignment', 'expected an identified user and scoped role assignments.');
  }
  return payload;
};
export const readMaintainerBulkMembersResponse = payload => {
  const data = payload?.data;
  if (!isRecord(payload) || payload.success !== true || !isRecord(data)
    || !Number.isInteger(data.createdCount) || data.createdCount < 0
    || !Array.isArray(data.memberIds) || data.memberIds.length !== data.createdCount
    || !data.memberIds.every(id => typeof id === 'string' && id.length > 0)) {
    throw new MaintainerResponseContractError('bulk member creation', 'expected a created count matching the returned member ids.');
  }
  return payload;
};
export const readMaintainerMemberDeleteResponse = payload => {
  const data = payload?.data;
  if (!isRecord(payload) || payload.success !== true || !isRecord(data)
    || typeof data.deletedMemberId !== 'string' || !data.deletedMemberId
    || typeof data.bahaiId !== 'string' || !data.bahaiId) {
    throw new MaintainerResponseContractError('member deletion', 'expected the deleted member id and Bahai ID.');
  }
  return payload;
};
export const readMaintainerSentryDebugResponse = payload => {
  if (!isRecord(payload) || payload.message !== 'Sentry verification event captured') {
    throw new MaintainerResponseContractError('Sentry verification', 'expected the explicit captured-event acknowledgement.');
  }
  return payload;
};

const isCommunity = community => isRecord(community) && typeof community._id === 'string'
  && community._id.length > 0 && typeof community.name === 'string' && isRecord(community.settings) && isOptionalTimeZone(community.timeZone);
export const readMaintainerCommunitiesResponse = payload => {
  if (!isRecord(payload) || payload.success !== true || !Array.isArray(payload.data)
    || !payload.data.every(community => isCommunity(community) && isCount(community.memberCount)
      && typeof community.locationSummary === 'string')) {
    throw new MaintainerResponseContractError('communities', 'expected enriched community records with member counts and location summaries.');
  }
  return payload;
};
export const readMaintainerCommunityResponse = payload => {
  if (!isRecord(payload) || payload.success !== true || !isCommunity(payload.data)) {
    throw new MaintainerResponseContractError('community', 'expected a saved community with settings.');
  }
  return payload;
};
export const readMaintainerCommunityDetailResponse = payload => {
  const data = payload?.data;
  const validMember = member => isRecord(member) && typeof member._id === 'string'
    && typeof member.firstName === 'string' && typeof member.lastName === 'string'
    && typeof member.type === 'string' && typeof member.userModel === 'string';
  if (!isRecord(payload) || payload.success !== true || !isRecord(data) || !isCommunity(data.community)
    || !Array.isArray(data.members) || !data.members.every(validMember)) {
    throw new MaintainerResponseContractError('community detail', 'expected the community and member directory records.');
  }
  return payload;
};

const enc = (value) => encodeURIComponent(value);

export const createMaintainerClient = (adapter) => {
  if (!adapter || typeof adapter.get !== 'function') {
    throw new TypeError('createMaintainerClient requires an adapter with a get method.');
  }

  return {
    getMe(options = {}) {
      return adapter.get(MAINTAINER_PATHS.me, options).then(readMaintainerMeResponse);
    },
    listCountries(options = {}) {
      return adapter.get(MAINTAINER_PATHS.countries, options).then(readMaintainerCountriesResponse);
    },
    getOverviewStatistics(options = {}) {
      return adapter.get(MAINTAINER_PATHS.overviewStatistics, options).then(readMaintainerOverviewStatisticsResponse);
    },
    getMetricsSummary(params = {}, options = {}) {
      return adapter.get(MAINTAINER_PATHS.summaryMetrics, {
        ...options,
        ...(Object.keys(params).length ? { params } : {}),
      }).then(readMaintainerMetricsSummaryResponse);
    },
    ingestRum(payload, options = {}) {
      return adapter.post(MAINTAINER_PATHS.rumMetrics, payload, options).then(readMaintainerRumIngestResponse);
    },
    updateMember(memberId, payload, options = {}) {
      return adapter.patch(buildMaintainerMemberPath(memberId), payload, options).then(readMaintainerMemberResponse);
    },
    assignRole(payload, options = {}) {
      return adapter.post(`${MAINTAINER_PATHS.roles}/assign`, payload, options).then(readMaintainerRoleAssignmentResponse);
    },
    listCommunities(params = {}, options = {}) {
      return adapter.get(MAINTAINER_PATHS.communities, {
        ...options,
        ...(Object.keys(params).length ? { params } : {}),
      }).then(readMaintainerCommunitiesResponse);
    },
    getCommunity(communityId, options = {}) {
      return adapter.get(buildMaintainerCommunityPath(communityId), options).then(readMaintainerCommunityDetailResponse);
    },
    createCommunity(payload, options = {}) {
      return adapter.post(MAINTAINER_PATHS.communities, readCommunityTimeZoneRequest(payload), options).then(readMaintainerCommunityResponse);
    },
    updateCommunity(communityId, payload, options = {}) {
      return adapter.patch(buildMaintainerCommunityPath(communityId), readCommunityTimeZoneRequest(payload), options).then(readMaintainerCommunityResponse);
    },
    updateCommunitySettings(communityId, payload, options = {}) {
      return adapter.patch(buildMaintainerCommunitySettingsPath(communityId), readCommunityTimeZoneRequest(payload), options).then(readMaintainerCommunityResponse);
    },
    createCommunityMember(communityId, payload, options = {}) {
      return adapter.post(buildMaintainerCommunityMembersPath(communityId), payload, options).then(readMaintainerMemberResponse);
    },
    bulkCreateCommunityMembers(communityId, payload, options = {}) {
      return adapter.post(buildMaintainerCommunityMembersBulkPath(communityId), payload, options).then(readMaintainerBulkMembersResponse);
    },
    deleteCommunityMember(communityId, bahaiId, options = {}) {
      return adapter.delete(buildMaintainerCommunityMemberByBahaiIdPath(communityId, bahaiId), options).then(readMaintainerMemberDeleteResponse);
    },
    triggerSentryVerification(options = {}) {
      return adapter.post('/api/maintainer/debug/sentry', {}, options).then(readMaintainerSentryDebugResponse);
    },
    search(query, options = {}) {
      return adapter.get(MAINTAINER_PATHS.search, {
        ...options,
        params: { ...options.params, q: query },
      }).then(readMaintainerSearchResponse);
    },
    listRoles(options = {}) {
      return adapter.get(MAINTAINER_PATHS.roles, options).then(readMaintainerRolesResponse);
    },
    createRole(payload, options = {}) {
      return adapter.post(MAINTAINER_PATHS.roles, payload, options).then(readMaintainerRoleResponse);
    },
    updateRole(roleId, payload, options = {}) {
      return adapter.patch(buildMaintainerRolePath(roleId), payload, options).then(readMaintainerRoleResponse);
    },
    listRoleUsers(roleId, options = {}) {
      return adapter.get(buildMaintainerRoleUsersPath(roleId), options).then(readMaintainerRoleUsersResponse);
    },
    createCountry(payload, options = {}) {
      return adapter.post(MAINTAINER_PATHS.countries, payload, options).then(readMaintainerCountryResponse);
    },
    updateCountry(countryId, payload, options = {}) {
      return adapter.patch(buildMaintainerCountryPath(countryId), payload, options).then(readMaintainerCountryResponse);
    },
    createState(payload, options = {}) {
      return adapter.post(MAINTAINER_PATHS.states, payload, options).then(readMaintainerStateResponse);
    },
    updateState(stateId, payload, options = {}) {
      return adapter.patch(buildMaintainerStatePath(stateId), payload, options).then(readMaintainerStateResponse);
    },
    createRegion(payload, options = {}) {
      return adapter.post(MAINTAINER_PATHS.regions, payload, options).then(readMaintainerRegionResponse);
    },
    updateRegion(regionId, payload, options = {}) {
      return adapter.patch(buildMaintainerRegionPath(regionId), payload, options).then(readMaintainerRegionResponse);
    },
    createCluster(payload, options = {}) {
      return adapter.post(MAINTAINER_PATHS.clusters, payload, options).then(readMaintainerClusterResponse);
    },
    updateCluster(clusterId, payload, options = {}) {
      return adapter.patch(buildMaintainerClusterPath(clusterId), payload, options).then(readMaintainerClusterResponse);
    },
    listBodies(options = {}) {
      return adapter.get(MAINTAINER_PATHS.bodies, options).then(readMaintainerBodiesResponse);
    },
    getBody(bodyId, options = {}) {
      return adapter.get(buildMaintainerBodyPath(bodyId), options).then(readMaintainerBodyDetailResponse);
    },
    createBody(payload, options = {}) {
      return adapter.post(MAINTAINER_PATHS.bodies, payload, options).then(readMaintainerBodyResponse);
    },
    updateBody(bodyId, payload, options = {}) {
      return adapter.patch(buildMaintainerBodyPath(bodyId), payload, options).then(readMaintainerBodyResponse);
    },
    assignBodyMembership(bodyId, payload, options = {}) {
      return adapter.post(buildMaintainerBodyMembershipsPath(bodyId), payload, options).then(readMaintainerBodyDetailResponse);
    },
  };
};

export const buildMaintainerSearchPath = (query, limit) => {
  const params = new URLSearchParams();
  const normalizedQuery = String(query || '').trim();
  if (normalizedQuery) params.set('q', normalizedQuery);
  if (limit !== undefined && limit !== null && limit !== '') params.set('limit', String(limit));
  return `${MAINTAINER_PATHS.search}${params.size ? `?${params}` : ''}`;
};

export const buildMaintainerCommunityPath = (communityId) =>
  `${MAINTAINER_PATHS.communities}/${enc(communityId)}`;
export const buildMaintainerCommunitySettingsPath = (communityId) =>
  `${buildMaintainerCommunityPath(communityId)}/settings`;
export const buildMaintainerCommunityMembersPath = (communityId) =>
  `${buildMaintainerCommunityPath(communityId)}/members`;
export const buildMaintainerCommunityMemberByBahaiIdPath = (communityId, bahaiId) =>
  `${buildMaintainerCommunityMembersPath(communityId)}/by-bahai-id/${enc(bahaiId)}`;
export const buildMaintainerCommunityMembersBulkPath = (communityId) =>
  `${buildMaintainerCommunityMembersPath(communityId)}/bulk`;
export const buildMaintainerMemberPath = (memberId) =>
  `/api/maintainer/members/${enc(memberId)}`;

export const buildMaintainerCountryPath = (countryId) =>
  `${MAINTAINER_PATHS.countries}/${enc(countryId)}`;
export const buildMaintainerStatePath = (stateId) =>
  `${MAINTAINER_PATHS.states}/${enc(stateId)}`;
export const buildMaintainerRegionPath = (regionId) =>
  `${MAINTAINER_PATHS.regions}/${enc(regionId)}`;
export const buildMaintainerClusterPath = (clusterId) =>
  `${MAINTAINER_PATHS.clusters}/${enc(clusterId)}`;

export const buildMaintainerBodyPath = (bodyId) =>
  `${MAINTAINER_PATHS.bodies}/${enc(bodyId)}`;
export const buildMaintainerBodyMembershipsPath = (bodyId) =>
  `${buildMaintainerBodyPath(bodyId)}/memberships`;

export const buildMaintainerRolePath = (roleId) =>
  `${MAINTAINER_PATHS.roles}/${enc(roleId)}`;
export const buildMaintainerRoleUsersPath = (roleId) =>
  `${buildMaintainerRolePath(roleId)}/users`;
export const buildMaintainerRoleAssignPath = () =>
  `${MAINTAINER_PATHS.roles}/assign`;
